import React, { useState, useEffect, useMemo, FC, ChangeEvent, forwardRef, useImperativeHandle } from 'react';
import Select, { SingleValue } from 'react-select';
import { Country, State } from 'country-state-city';
import PhoneInput, { Value } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { MinusCircleIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { AnalyticsParamBuilder, OnTextBlurAnalytics } from '@Utils/common';
import posthog from 'posthog-js';

// ## Type Definitions ##

// Type for react-select options
interface SelectOption {
    value: string;
    label: string;
}

// Type for the entire form state
interface FormDataState {
    fullName: string;
    phoneNumber: Value | undefined;
    country: SelectOption | null;
    // State can be a SelectOption (for India) or a string (for other countries)
    state: SelectOption | string | null;
    isBusiness: boolean;
    gstin: string;
    legalBusinessName: string;
    address1: string;
    address2: string;
    postalCode: string;
    city: string;
}
// Add below `interface BillingFormProps { ... }`
type FormErrors = Partial<Record<keyof FormDataState, string>>;
type Touched = Partial<Record<keyof FormDataState, boolean>>;

// Type for the component's props
interface BillingFormProps {
    userData: {
        first_name?: string;
        last_name?: string;
        phone_number?: string;
    };
    onDataChange: (data: FormDataState) => void;
}

const COMPONENT = 'billing_form';

// ## Main Component ##
const BillingForm = forwardRef<{ validateForm: () => boolean }, BillingFormProps>(({ onDataChange, userData }, ref) => {


    // State to manage all form data, now strongly typed
    const [formData, setFormData] = useState<FormDataState>({
        fullName: `${userData?.first_name || ''} ${userData?.last_name || ''}`,
        phoneNumber: userData?.phone_number || undefined,
        country: null,
        state: null,
        isBusiness: false,
        gstin: '',
        legalBusinessName: '',
        address1: '',
        address2: '',
        postalCode: '',
        city: '',
    });

    // State to toggle the visibility of business details
    const [showBusinessDetails, setShowBusinessDetails] = useState(false);

    const [errors, setErrors] = useState<FormErrors>({});
    const [touched, setTouched] = useState<Touched>({});

    // Memoize country options to avoid recalculating on every render
    const countryOptions: SelectOption[] = useMemo(() => {
        const allCountries = Country.getAllCountries().map(country => ({
            value: country.isoCode,
            label: country.name,
        }));

        const indiaIndex = allCountries.findIndex(c => c.label.toLowerCase() === 'india');

        if (indiaIndex > -1) {
            const [india] = allCountries.splice(indiaIndex, 1);
            return [india, ...allCountries];
        }

        // If India is not found, just return the full list
        return allCountries;
    }, []);


    // Memoize state options for India
    const stateOptions: SelectOption[] = useMemo(() => {
        if (formData.country?.value !== 'IN') return [];
        return State.getStatesOfCountry('IN').map(state => ({
            value: state.isoCode,
            label: state.name,
        }));
    }, [formData.country]);

    const isIndia = formData.country?.value === 'IN';

    const validate = (data: FormDataState, businessOn: boolean): FormErrors => {
        const e: FormErrors = {};

        // Always-required
        if (!data.fullName?.trim()) e.fullName = 'Full name is required.';
        if (!data.phoneNumber) e.phoneNumber = 'Phone number is required.';
        if (!data.country) e.country = 'Please select a country.';

        // State logic
        if (data.country) {
            if (isIndia) {
                if (!data.state || typeof data.state !== 'object') e.state = 'Please select a state.';
            } else {
                if (!data.state || typeof data.state !== 'string' || !data.state.trim())
                    e.state = 'State/Province is required.';
            }
        }

        // Business-only (validate **only** when business is on)
        if (businessOn) {
            if (!data.legalBusinessName?.trim()) e.legalBusinessName = 'Legal business name is required.';
            if (!data.address1?.trim()) e.address1 = 'Address line 1 is required.';
            if (!data.city?.trim()) e.city = 'City is required.';
            if (!data.postalCode?.trim()) e.postalCode = 'Postal code is required.';

            // GSTIN only if India
            if (isIndia) {
                const gstin = data.gstin?.trim() || '';
                const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/;
                if (!gstin) e.gstin = 'GSTIN is required for Indian businesses.';
                else if (!GSTIN_REGEX.test(gstin)) e.gstin = 'Enter a valid GSTIN.';
            }
        }

        return e;
    };

    // Effect to call the parent's callback function whenever formData changes
    useEffect(() => {
        onDataChange(formData);
        setErrors(validate(formData, showBusinessDetails));
    }, [formData, showBusinessDetails, onDataChange]);


    // Generic handler for text inputs
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const markTouched = (name: keyof FormDataState) => {
        setTouched(prev => ({ ...prev, [name]: true }));
    };


    // Handler for react-select (Country)
    const handleCountryChange = (selectedOption: SingleValue<SelectOption>) => {
        setFormData(prev => ({
            ...prev,
            country: selectedOption,
            state: null, // Reset state when country changes
        }));
    };

    // Handler for State (handles both react-select and text input)
    const handleStateChange = (value: SingleValue<SelectOption> | ChangeEvent<HTMLInputElement>) => {
        // Type guard to check if it's a standard input event or a react-select value
        if (value && 'target' in value) {
            setFormData(prev => ({ ...prev, state: value.target.value }));
        } else {
            setFormData(prev => ({ ...prev, state: value }));
        }
    };


    // Handler for PhoneInput
    const handlePhoneChange = (value: Value | undefined) => {
        setFormData(prev => ({ ...prev, phoneNumber: value }));
    };

    // Handler for toggling business details
    const toggleBusinessDetails = () => {
        posthog.capture("ui_action", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "toggle_business",
            classValue: !showBusinessDetails ? "show" : "hide",
        }));
        const isShowing = !showBusinessDetails;
        setShowBusinessDetails(isShowing);
        setFormData(prev => ({
            ...prev,
            isBusiness: isShowing,
            ...(isShowing
                ? {}
                : {
                    gstin: '',
                    legalBusinessName: '',
                    address1: '',
                    address2: '',
                    postalCode: '',
                    city: '',
                }),
        }));
        if (!isShowing) {
            // also clear touched & errors for business-only fields
            setTouched(prev => {
                const { gstin, legalBusinessName, address1, address2, postalCode, city, ...rest } = prev;
                return rest;
            });
            setErrors(prev => {
                const { gstin, legalBusinessName, address1, address2, postalCode, city, ...rest } = prev;
                return rest;
            });
        }
    };



    // Common Tailwind classes for inputs
    const inputClasses = "w-full p-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:border-[#6355ff] outline-none transition-all duration-200 placeholder-gray-400";

    // Custom styles for react-select to match the design
    const customSelectStyles = {
        control: (provided: any, state: { isFocused: boolean; }) => ({
            ...provided,
            padding: '0.3rem',
            backgroundColor: '#F9FAFB',
            border: state.isFocused ? '1px solid #6355ff' : '1px solid #D1D5DB',
            borderRadius: '0.5rem',
            boxShadow: state.isFocused ? '0 0 0 2px #c4b5fd' : 'none',
            '&:hover': {
                borderColor: '#b5aefeff',
            },
        }),
        option: (provided: any, state: { isSelected: boolean; isFocused: boolean; }) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#6355ff' : state.isFocused ? '#b5aefeff' : 'white',
            color: state.isSelected ? 'white' : 'black',
        }),
    };

    const ErrorText: FC<{ name: keyof FormDataState }> = ({ name }) =>
        touched[name] && errors[name] ? (
            <p className="text-sm text-red-600 mt-1">{errors[name]}</p>
        ) : null;


    useImperativeHandle(ref, () => ({
        validateForm: () => {
            const touchedFields: Touched = {
                fullName: true,
                phoneNumber: true,
                country: true,
                state: true,
            };
            if (showBusinessDetails) {
                touchedFields.gstin = true;
                touchedFields.legalBusinessName = true;
                touchedFields.address1 = true;
                touchedFields.postalCode = true;
                touchedFields.city = true;
            }
            setTouched(touchedFields);

            const validationErrors = validate(formData, showBusinessDetails);
            setErrors(validationErrors);

            return Object.keys(validationErrors).length === 0;
        }
    }));



    return (
        <div className="max-w-4xl mx-auto">
            <style>
                {/* Styles provided for the phone number input */}
                {`
                    .PhoneInput {
                        display: flex;
                        align-items: center;
                        width: 100%;
                        border-radius: 0.5rem;
                        background-color: #f9fafb;
                        border: 1px solid #d1d5db;
                    }
                    .PhoneInput:focus-within {
                        outline: none;
                        border-color: #6355ff;
                        box-shadow: 0 0 0 2px #c4b5fd;
                    }
                    .PhoneInputInput {
                        border: none;
                        background: transparent;
                        padding: 0.75rem;
                        outline: none;
                        flex: 1;
                        font-size: 1rem;
                        color: #111827;
                    }
                    .PhoneInputCountry {
                        margin-left: 0.75rem;
                        display: flex;
                        align-items: center;
                    }
                    .PhoneInputCountryIcon {
                        box-shadow: none;
                    }
                `}
            </style>

            <form className="space-y-6">
                {/* Section: Billed to */}
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Billed to</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                            <input
                                type="text"
                                name="fullName"
                                placeholder="Your full name"
                                value={formData.fullName}
                                onChange={handleChange}
                                className={inputClasses}
                                onBlur={() => {
                                    OnTextBlurAnalytics(COMPONENT, 'first_name', formData.fullName, 'text');
                                    markTouched('fullName')
                                }}

                            />
                            <ErrorText name="fullName" />
                        </div>

                        <div>
                            <PhoneInput
                                placeholder="Enter your phone number"
                                defaultCountry="IN"
                                value={formData.phoneNumber}
                                onChange={handlePhoneChange}
                                international
                                onBlur={() => {
                                    OnTextBlurAnalytics(COMPONENT, 'phone_number', `${formData.phoneNumber}`, 'text');
                                    markTouched('phoneNumber')
                                }}
                            />
                            <ErrorText name="phoneNumber" />
                        </div>
                    </div>
                </div>

                {/* Section: Billing Details */}
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">Billing Details</h2>
                        <button
                            type="button"
                            onClick={toggleBusinessDetails}
                            className="text-sm flex items-center gap-2 text-[#6355ff] font-medium hover:text-[#4838fc] transition-colors"
                        >
                            {showBusinessDetails ? <MinusCircleIcon className='size-3' /> : <PlusCircleIcon className='size-3' />}
                            {showBusinessDetails ? 'Hide Business Details' : 'Add Business Details'}
                        </button>
                    </div>

                    <div className="space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                                <Select
                                    options={countryOptions}
                                    value={formData.country}
                                    onChange={handleCountryChange}
                                    placeholder="Country"
                                    styles={customSelectStyles}
                                    isSearchable
                                    onBlur={() => {
                                        OnTextBlurAnalytics(COMPONENT, 'country', JSON.stringify(formData.country), 'select');
                                        markTouched('country')
                                    }}
                                />
                                <ErrorText name="country" />
                            </div>
                            <div>
                                {formData.country?.value === 'IN' ? (
                                    <Select
                                        options={stateOptions}
                                        value={formData.state as SingleValue<SelectOption>}
                                        onChange={handleStateChange}
                                        placeholder="State"
                                        styles={customSelectStyles}
                                        onBlur={() => {
                                            OnTextBlurAnalytics(COMPONENT, 'state', JSON.stringify(formData.state), 'select');
                                            markTouched('state')
                                        }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        name="state"
                                        placeholder="State / Province"
                                        value={typeof formData.state === 'string' ? formData.state : ''}
                                        onChange={handleStateChange}
                                        className={inputClasses}
                                        disabled={!formData.country}
                                        onBlur={() => {
                                            OnTextBlurAnalytics(COMPONENT, 'state', `${formData.state}`, 'text');
                                            markTouched('state')
                                        }}
                                    />
                                )}
                                <ErrorText name="state" />
                            </div>
                        </div>

                        {showBusinessDetails && (
                            <div className="space-y-2 animate-fade-in">
                                <div>
                                    {(formData.country?.value == 'IN') && <input
                                        type="text"
                                        name="gstin"
                                        placeholder="GSTIN: 09AAXXX1234A1ZA"
                                        value={formData.gstin}
                                        onChange={handleChange}
                                        className={inputClasses}
                                        onBlur={() => {
                                            OnTextBlurAnalytics(COMPONENT, 'gstin', `${formData.gstin}`, 'text');
                                            markTouched('gstin')
                                        }}
                                    />}
                                    <ErrorText name="gstin" />
                                </div>
                                <div>
                                    <input
                                        type="text"
                                        name="legalBusinessName"
                                        placeholder="Legal Business Name"
                                        value={formData.legalBusinessName}
                                        onChange={handleChange}
                                        className={inputClasses}
                                        onBlur={() => {
                                            OnTextBlurAnalytics(COMPONENT, 'legalBusinessName', `${formData.legalBusinessName}`, 'text');
                                            markTouched('legalBusinessName')
                                        }}
                                    />
                                    <ErrorText name="legalBusinessName" />
                                </div>
                                <div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                                        <div>
                                            <input
                                                type="text"
                                                name="address1"
                                                placeholder="Address Line 1"
                                                value={formData.address1}
                                                onChange={handleChange}
                                                className={inputClasses}
                                                onBlur={() => {
                                                    OnTextBlurAnalytics(COMPONENT, 'address1', `${formData.address1}`, 'text');
                                                    markTouched('address1')
                                                }}
                                            />
                                            <ErrorText name="address1" />
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                name="address2"
                                                placeholder="Address Line 2"
                                                value={formData.address2}
                                                onChange={handleChange}
                                                className={inputClasses}
                                                onBlur={() => {
                                                    OnTextBlurAnalytics(COMPONENT, 'address2', `${formData.address2}`, 'text');
                                                    markTouched('address2')
                                                }}
                                            />
                                            <ErrorText name="address2" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ">
                                        <div>
                                            <input
                                                type="text"
                                                name="postalCode"
                                                placeholder="Postal Code"
                                                value={formData.postalCode}
                                                onChange={handleChange}
                                                className={inputClasses}
                                                onBlur={() => {
                                                    OnTextBlurAnalytics(COMPONENT, 'postalCode', `${formData.postalCode}`, 'text');
                                                    markTouched('postalCode')
                                                }}
                                            />
                                            <ErrorText name="postalCode" />
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                name="city"
                                                placeholder="City"
                                                value={formData.city}
                                                onChange={handleChange}
                                                className={inputClasses}
                                                onBlur={() => {
                                                    OnTextBlurAnalytics(COMPONENT, 'city', `${formData.city}`, 'text');
                                                    markTouched('city')
                                                }}
                                            />
                                            <ErrorText name="city" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
});

export default BillingForm;